<template>
	<Drawer.Root
		:open="true"
		:modal="false"
		:trap-focus="false"
		:restore-focus="false"
		:close-on-interact-outside="false"
		:snap-points="[collapsedSnapPoint, peekHeight, 1]"
		v-model:snap-point="snapPoint"
		snap-to-sequential-points
		@update:open="onOpenChange"
	>
		<Drawer.Backdrop class="mobile-drawer-backdrop" />
		<Drawer.Positioner class="mobile-drawer-positioner">
			<Drawer.Content class="mobile-drawer-content">
				<Drawer.Grabber class="mobile-drawer-grabber">
					<Drawer.GrabberIndicator class="mobile-drawer-grabber-indicator" />
				</Drawer.Grabber>
				<slot :expanded="isExpanded" />
			</Drawer.Content>
		</Drawer.Positioner>
	</Drawer.Root>

	<button
		v-if="active && modelValue"
		type="button"
		class="mobile-drawer-reopen-btn"
		:style="{ top: reopenTop }"
		:title="reopenTitle"
		@click="emit('update:modelValue', false)"
	>
		<AppIcon :name="IconName.Info" />
	</button>
</template>

<script setup lang="ts">
// Stays mounted and always "open" (see :active) so Ark's Drawer.Root never has to re-measure from a
// cold/empty state; callers must stay mounted too (never `v-if="... && selectedItem"`) for the same reason.
import { ref, computed, watch } from "vue"
import { Drawer } from "@ark-ui/vue/drawer"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"

const props = defineProps<{
	modelValue: boolean // collapsed (peek-hidden, showing only the reopen button)
	active: boolean // whether there's currently anything to show
	peekHeight: string // e.g. the slotted content's own header height in pixels ("220px")
	reopenTitle?: string
	reopenTop?: string
	expand?: boolean // snap to full height on request (e.g. a sub-view opened inside the sheet)
}>()

const emit = defineEmits<{
	"update:modelValue": [collapsed: boolean]
	dismiss: [] // closed entirely (swiped past every snap point, or Escape) -- treat as a real dismissal
}>()

const collapsedSnapPoint = "0px"
const isEffectivelyCollapsed = computed(() => !props.active || props.modelValue)
const snapPoint = ref<string | number>(isEffectivelyCollapsed.value ? collapsedSnapPoint : props.peekHeight)
// dragged past the peek snap point toward the full-height snap point
const isExpanded = computed(
	() => !isEffectivelyCollapsed.value && snapPoint.value !== collapsedSnapPoint && snapPoint.value !== props.peekHeight
)

watch(isEffectivelyCollapsed, collapsed => {
	snapPoint.value = collapsed ? collapsedSnapPoint : props.peekHeight
})

// follow the peek height as it's (re)measured, unless dragged away from peek or collapsed
watch(
	() => props.peekHeight,
	(newPeek, oldPeek) => {
		if (!isEffectivelyCollapsed.value && snapPoint.value === oldPeek) {
			snapPoint.value = newPeek
		}
	}
)

// drop back to the peek when the request clears, unless the user had already expanded it
let wasExpandedBeforeRequest = false
watch(
	() => props.expand,
	want => {
		if (isEffectivelyCollapsed.value) return
		if (want) {
			wasExpandedBeforeRequest = isExpanded.value
			snapPoint.value = 1
		} else if (!wasExpandedBeforeRequest && snapPoint.value === 1) {
			snapPoint.value = props.peekHeight
		}
	}
)

// covers the user swiping to collapsedSnapPoint themselves, not just the caller's own collapse trigger
watch(snapPoint, sp => {
	if (!props.active) return
	const collapsed = sp === collapsedSnapPoint
	if (collapsed !== props.modelValue) {
		emit("update:modelValue", collapsed)
	}
})

function onOpenChange(open: boolean) {
	if (!open) emit("dismiss")
}
</script>

<style scoped>
.mobile-drawer-backdrop {
	position: fixed;
	inset: 0;
	z-index: 110;
	background: transparent;
	pointer-events: none;
}

.mobile-drawer-positioner {
	position: fixed;
	inset: 0;
	z-index: 110;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	width: 100vw;
	height: 100dvh;
	/* only the sheet itself catches pointer events, so the empty area (incl. the reopen button beneath) stays usable */
	pointer-events: none;
}

.mobile-drawer-content {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100dvh;
	overflow: hidden;
	border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
	background-color: var(--neutral-0);
	box-shadow: var(--shadow-lg);
	pointer-events: auto;
	transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);

	:slotted(*) {
		flex: 1;
		min-height: 0;
	}
}

.mobile-drawer-grabber {
	display: flex;
	flex-shrink: 0;
	justify-content: center;
	padding: var(--spacing-xs) 0;
	cursor: grab;
}

.mobile-drawer-grabber-indicator {
	width: 36px;
	height: 4px;
	border-radius: var(--border-radius-full);
	background-color: var(--neutral-400);
}

.mobile-drawer-reopen-btn {
	position: fixed;
	top: var(--spacing-md);
	right: var(--spacing-md);
	z-index: 115;
	display: flex;
	align-items: center;
	justify-content: center;
	width: var(--space-40);
	height: var(--space-40);
	border-radius: var(--border-radius-full);
	border: none;
	background: var(--neutral-0);
	color: var(--neutral-600);
	cursor: pointer;
	box-shadow: var(--shadow-lg);
}
</style>
